import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { PawPrint, HandHeart, ArrowRight } from "@phosphor-icons/react";
import api from "@/lib/api";

const fmt = (n) => Math.floor(n).toLocaleString();

function Counter({ value, label, sub, color, icon: Icon, testid }) {
  return (
    <div className="text-center px-4" data-testid={testid}>
      <div className="inline-flex items-center gap-2 mb-2">
        <Icon weight="fill" size={16} style={{ color }} />
        <span className="overline text-muted-foreground">{label}</span>
      </div>
      <p className="font-display font-black tabular-nums leading-none text-5xl sm:text-6xl lg:text-7xl tracking-tighter"
        style={{ color, textShadow: `0 0 26px ${color}66` }} data-testid={`${testid}-value`}>
        {fmt(value)}
      </p>
      <p className="overline text-muted-foreground/80 mt-2">{sub}</p>
    </div>
  );
}

export default function MissionTicker() {
  const [pits, setPits] = useState(null);
  const [kids, setKids] = useState(null);
  const rates = useRef({ pits: 0.08, kids: 0.05 });

  useEffect(() => {
    let alive = true;
    api.get("/mission/counters").then(({ data }) => {
      if (!alive) return;
      setPits(data.pits_saved);
      setKids(data.kids_reunited);
      rates.current = { pits: data.pits_saved_rate, kids: data.kids_reunited_rate };
    }).catch(() => {
      if (!alive) return;
      setPits(3128); setKids(8412);
    });
    const t = setInterval(() => {
      setPits((p) => (p == null ? p : p + rates.current.pits));
      setKids((k) => (k == null ? k : k + rates.current.kids));
    }, 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  if (pits == null || kids == null) return null;

  return (
    <section className="relative overflow-hidden border-y border-white/10 bg-[#050505]" data-testid="mission-ticker">
      <div className="absolute inset-0 pointer-events-none opacity-40"
        style={{ background: "radial-gradient(60% 120% at 50% 0%, rgba(255,20,147,0.16), transparent 60%)" }} />
      <div className="relative max-w-6xl mx-auto px-5 sm:px-8 py-14 sm:py-16 text-center">
        <motion.p initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="overline text-primary mb-3 inline-flex items-center gap-2">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" /> Every drop runs for a reason
        </motion.p>
        <motion.h2 initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          className="font-display font-black uppercase text-3xl sm:text-4xl lg:text-5xl tracking-tighter mb-10 max-w-3xl mx-auto leading-[0.95]">
          This isn't just fashion.<br /><span className="neon-text">These numbers climb every second.</span>
        </motion.h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-10 sm:gap-6 items-center">
          <Counter value={pits} label="Pits given a second chance" sub="Rescued · fostered · rehomed"
            color="#ff1493" icon={PawPrint} testid="ticker-pits" />
          <Counter value={kids} label="Children reunited & protected" sub="Bring them home"
            color="#00e5e5" icon={HandHeart} testid="ticker-kids" />
        </div>

        <p className="text-muted-foreground mt-10 max-w-xl mx-auto leading-relaxed">
          Behind every hoodie is a pit off the street and a kid one step closer to home. You don't just wear it — you move the number.
        </p>
        <div className="mt-7 flex flex-wrap gap-4 justify-center">
          <Link to="/shop" className="metal-cta inline-flex items-center gap-2 bg-primary text-primary-foreground px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-primary/90 transition-colors" data-testid="ticker-shop-cta">
            <PawPrint weight="fill" size={17} className="tail-wiggle" /> Move the number — Shop
          </Link>
          <Link to="/donate" className="inline-flex items-center gap-2 border border-secondary/60 text-secondary px-7 py-3.5 rounded-[10px] font-modern font-medium hover:bg-secondary/10 transition-colors" data-testid="ticker-donate-cta">
            Stand in the gap — Donate <ArrowRight weight="bold" size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}
